<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from 'vue'
import axios from 'axios'

const props = defineProps({
  tableName: String,
  record: Object,
  columns: Array,
  show: Boolean,
  isNew: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'saved'])

const editingRecord = ref({})
const loading = ref(false)
const searchQuery = ref('')

const filteredFields = computed(() => {
  if (!searchQuery.value) return props.columns || []
  
  return (props.columns || []).filter(column => 
    column.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const initializeRecord = () => {
  if (props.isNew) {
    // Initialize new record with default values
    editingRecord.value = {}
    props.columns?.forEach(column => {
      if (!column.autoIncrement) {
        editingRecord.value[column.name] = column.default || ''
      }
    })
  } else {
    // Copy existing record
    editingRecord.value = { ...props.record }
  }
}

const saveRecord = async () => {
  loading.value = true
  try {
    if (props.isNew) {
      await axios.post(`/api/database-tables/${props.tableName}/data`, editingRecord.value)
    } else {
      const primaryKey = props.columns?.find(col => col.key === 'PRI')?.name
      if (!primaryKey) {
        alert('No primary key found for this table')
        return
      }
      await axios.put(`/api/database-tables/${props.tableName}/data/${editingRecord.value[primaryKey]}`, editingRecord.value)
    }
    
    emit('saved')
    emit('close')
  } catch (error) {
    console.error('Error saving record:', error)
    alert('Error saving record: ' + (error.response?.data?.message || error.message))
  } finally {
    loading.value = false
  }
}

const closeEditor = () => {
  emit('close')
}

const getFieldType = (column) => {
  const type = column.type.toLowerCase()
  if (type.includes('int') || type.includes('decimal') || type.includes('float') || type.includes('double')) {
    return 'number'
  }
  if (type.includes('date') || type.includes('time')) {
    return 'datetime-local'
  }
  if (type.includes('text') || type.includes('longtext')) {
    return 'textarea'
  }
  return 'text'
}

const formatFieldType = (column) => {
  return column.type + (column.nullable ? '' : ' NOT NULL')
}

watch(() => props.show, (newVal) => {
  if (newVal) {
    initializeRecord()
  }
})

watch(() => props.record, () => {
  if (props.show) {
    initializeRecord()
  }
})

onMounted(() => {
  if (props.show) {
    initializeRecord()
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/50 z-50 flex">
    <!-- Right Side Panel -->
    <div class="ml-auto w-96 bg-white h-full flex flex-col shadow-xl">
      <!-- Header -->
      <div class="bg-gray-50 border-b border-gray-200 p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ isNew ? 'Add New Record' : 'Edit Record' }}
          </h3>
          <button 
            @click="closeEditor"
            class="text-gray-500 hover:text-gray-700 p-1"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Search Fields -->
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search for field..."
            class="w-full pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
          <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
        </div>
      </div>

      <!-- Fields List -->
      <div class="flex-1 overflow-y-auto">
        <div class="p-4 space-y-3">
          <div 
            v-for="column in filteredFields" 
            :key="column.name"
            class="bg-gray-50 rounded-lg p-3"
          >
            <!-- Field Header -->
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center">
                <span class="font-medium text-gray-900 text-sm">{{ column.name }}</span>
                <span v-if="column.key === 'PRI'" class="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                  PK
                </span>
                <span v-if="!column.nullable" class="ml-1 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                  Required
                </span>
              </div>
              <span class="text-xs text-gray-500">{{ formatFieldType(column) }}</span>
            </div>

            <!-- Field Input -->
            <div>
              <textarea
                v-if="getFieldType(column) === 'textarea'"
                v-model="editingRecord[column.name]"
                :disabled="column.autoIncrement"
                :placeholder="column.default ? `Default: ${column.default}` : ''"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                :class="{ 'bg-gray-100': column.autoIncrement }"
                rows="3"
              ></textarea>
              
              <input
                v-else
                v-model="editingRecord[column.name]"
                :type="getFieldType(column)"
                :disabled="column.autoIncrement"
                :placeholder="column.default ? `Default: ${column.default}` : ''"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                :class="{ 'bg-gray-100': column.autoIncrement }"
              >
            </div>

            <!-- Auto Increment Notice -->
            <div v-if="column.autoIncrement" class="mt-1 text-xs text-gray-500">
              <i class="fas fa-info-circle mr-1"></i>
              Auto-generated value
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="bg-gray-50 border-t border-gray-200 p-4">
        <div class="flex space-x-3">
          <button
            @click="closeEditor"
            class="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            @click="saveRecord"
            :disabled="loading"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isNew ? 'Create' : 'Update' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 